// PDF of one AI Presence analysis (product or brand) — light layout, one
// flowing document, same react-pdf rules as ConsumerVoicePdf: stacked Texts
// never sit beside a flex:1 sibling inside a centered row, opaque hex colors
// only, fonts registered per weight (registerPdfFonts).
import React from 'react';
import { Document, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { registerPdfFonts } from '../../utils';
import {
  AIP_OPPORTUNITY_TYPE_LABELS,
  AIP_PLATFORM_LABELS,
  AiPresenceAudit,
  AiPresenceBrandReport,
  AiPresenceProductReport,
  AipOpportunity,
  aipSubjectLabel,
} from '../../types/aiPresence';

registerPdfFonts();

const GOLD = '#b8966a';
const INK = '#1C1A17';
const MUTED = '#7A7268';
const FAINT = '#9B958C';
const BORDER = '#E5E1DB';
const TRACK = '#F1EEE9';
const GREEN = '#2E7D32';
const RED = '#c62828';

const s = StyleSheet.create({
  page: { padding: 40, paddingBottom: 56, fontFamily: 'Lato', fontSize: 10, color: INK, lineHeight: 1.45 },
  kicker: { fontFamily: 'Oswald-Medium', fontSize: 9, color: GOLD, letterSpacing: 1, textTransform: 'uppercase' },
  title: { fontFamily: 'Oswald-Bold', fontSize: 22, marginTop: 4 },
  meta: { fontSize: 9, color: MUTED, marginTop: 4 },
  strip: { flexDirection: 'row', marginTop: 16 },
  card: { flex: 1, borderWidth: 1, borderColor: BORDER, borderRadius: 6, padding: 10, marginRight: 8 },
  cardLast: { marginRight: 0 },
  cardLabel: { fontFamily: 'Lato-Bold', fontSize: 8, color: FAINT, textTransform: 'uppercase', letterSpacing: 0.5 },
  cardValue: { fontFamily: 'Oswald-Bold', fontSize: 24, marginTop: 4 },
  cardCaption: { fontSize: 8, color: MUTED, marginTop: 4 },
  h2: { fontFamily: 'Oswald-Medium', fontSize: 13, marginTop: 18, marginBottom: 6, color: INK },
  note: { fontSize: 9, color: MUTED, marginBottom: 6 },
  item: { marginBottom: 6 },
  itemTitle: { fontFamily: 'Lato-Bold', fontSize: 10 },
  itemSub: { fontSize: 9, color: MUTED, marginTop: 1 },
  link: { color: GOLD, textDecoration: 'none' },
  row: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: BORDER, paddingVertical: 4 },
  rowHead: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: INK, paddingVertical: 4 },
  c1: { flex: 2.4, fontSize: 9 },
  c2: { flex: 1.1, fontSize: 9 },
  c2w: { flex: 1.5, fontSize: 9 },
  c3: { flex: 2, fontSize: 9, color: MUTED },
  bold: { fontFamily: 'Lato-Bold' },
  barRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  barName: { width: 190, fontSize: 9 },
  barTrack: { width: 220, height: 6, backgroundColor: TRACK, borderRadius: 3 },
  barFill: { height: 6, borderRadius: 3 },
  barValue: { width: 50, fontSize: 9, textAlign: 'right' },
  disclaimer: { fontSize: 8, color: FAINT, marginTop: 18 },
  footer: { position: 'absolute', bottom: 22, left: 40, right: 40, fontSize: 8, color: FAINT, flexDirection: 'row', justifyContent: 'space-between' },
});

const tone = (v: number | null | undefined) => {
  if (v == null) return MUTED;
  if (v >= 60) return GREEN;
  if (v >= 30) return GOLD;
  return RED;
};
const fmt = (n: number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const pct = (n: number | null | undefined) => (n == null ? '—' : `${n}%`);

function Bar({ name, value, color = GOLD }: { name: string; value: number | null | undefined; color?: string }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <View style={s.barRow} wrap={false}>
      <Text style={s.barName}>{name}</Text>
      <View style={s.barTrack}>
        <View style={[s.barFill, { width: `${v}%`, backgroundColor: color }]} />
      </View>
      <Text style={s.barValue}>{pct(value)}</Text>
    </View>
  );
}

function PlatformTable({ platforms }: { platforms: Record<string, any> }) {
  const keys = Object.keys(platforms || {});
  if (!keys.length) return null;
  return (
    <View>
      <View style={s.rowHead}>
        <Text style={[s.c1, s.bold]}>Platform</Text>
        <Text style={[s.c2, s.bold]}>AI Presence</Text>
        <Text style={[s.c2, s.bold]}>Discovery</Text>
        <Text style={[s.c2w, s.bold]}>Recommendation</Text>
        <Text style={[s.c3, s.bold]}>Model</Text>
      </View>
      {keys.map((k) => {
        const p = platforms[k];
        return (
          <View key={k} style={s.row} wrap={false}>
            <Text style={s.c1}>{p.label || AIP_PLATFORM_LABELS[k] || k}</Text>
            <Text style={[s.c2, s.bold, { color: tone(p.score) }]}>{fmt(p.score)}</Text>
            <Text style={s.c2}>{fmt(p.discovery)}</Text>
            <Text style={s.c2w}>{fmt(p.recommendation)}</Text>
            <Text style={s.c3}>{(p.models || []).join(', ') || (p.products_scored != null ? `${p.products_scored} products` : '')}</Text>
          </View>
        );
      })}
    </View>
  );
}

function Opportunities({ items }: { items: AipOpportunity[] }) {
  if (!items || !items.length) return <Text style={s.itemSub}>No open opportunities.</Text>;
  return (
    <View>
      {items.slice(0, 10).map((o) => (
        <View key={o.key} style={s.item} wrap={false}>
          <Text style={s.itemTitle}>
            {AIP_OPPORTUNITY_TYPE_LABELS[o.type] || o.type}
            {o.product ? ` · ${o.product}` : ''}: {o.title}
          </Text>
          <Text style={s.itemSub}>{o.detail}</Text>
          <Text style={s.itemSub}>Do this: {o.action}</Text>
        </View>
      ))}
    </View>
  );
}

function ProductPages({ report }: { report: AiPresenceProductReport }) {
  const platforms = Object.keys(report.platforms || {});
  const unbranded = report.prompts.filter((p) => !p.branded);
  const branded = report.prompts.filter((p) => p.branded);
  const sov = report.competitors;
  return (
    <>
      <Text style={s.h2}>By platform</Text>
      <PlatformTable platforms={report.platforms} />

      <Text style={s.h2}>Opportunities</Text>
      <Opportunities items={report.opportunities || []} />

      <Text style={s.h2}>{report.label || 'Estimated shopping prompts'}</Text>
      <Text style={s.note}>Unbranded prompts drive the scores. Columns: answers mentioning the product / answers judged, per platform.</Text>
      {unbranded.map((p) => (
        <View key={p.key} style={s.item} wrap={false}>
          <Text style={s.itemTitle}>{p.text}</Text>
          <Text style={s.itemSub}>
            {p.intent_label}
            {p.est_monthly_searches ? ` · ~${fmt(p.est_monthly_searches)} searches/mo` : ''}
            {' · '}
            {platforms
              .map((k) => {
                const x = p.platforms[k];
                return x ? `${AIP_PLATFORM_LABELS[k] || k} ${x.judged ? `${x.mentioned}/${x.judged}` : '—'}` : null;
              })
              .filter(Boolean)
              .join(' · ')}
          </Text>
          {platforms
            .map((k) => p.platforms[k] && p.platforms[k].summary)
            .filter(Boolean)
            .slice(0, 1)
            .map((summary, i) => (
              <Text key={i} style={s.itemSub}>
                {summary}
              </Text>
            ))}
        </View>
      ))}

      <Text style={s.h2}>Competitor visibility</Text>
      <Text style={s.note}>Share of the {sov.eligible} unbranded answers that name each product or brand.</Text>
      <Bar name={`${sov.us.name} (you)`} value={sov.us.appearance_rate} color={GREEN} />
      {sov.competitors.slice(0, 10).map((c) => (
        <Bar key={c.name} name={c.name} value={c.appearance_rate} />
      ))}

      <Text style={s.h2}>Cited sources</Text>
      <Text style={s.note}>
        Your product page was cited in {fmt(report.sources.own_pdp_cited_answers || 0)} answers. Sites AI relies on for this category:
      </Text>
      <View style={s.rowHead}>
        <Text style={[s.c1, s.bold]}>Site</Text>
        <Text style={[s.c2, s.bold]}>Answers</Text>
        <Text style={[s.c2, s.bold]}>With you</Text>
        <Text style={[s.c2, s.bold]}>Without you</Text>
      </View>
      {(report.sources.domains || []).slice(0, 12).map((d) => (
        <View key={d.domain} style={s.row} wrap={false}>
          <Text style={s.c1}>
            {d.domain}
            {d.own ? ' (yours)' : ''}
          </Text>
          <Text style={s.c2}>{d.answers}</Text>
          <Text style={s.c2}>{d.with_us}</Text>
          <Text style={s.c2}>{d.without_us}</Text>
        </View>
      ))}

      <Text style={s.h2}>What AI says about the product</Text>
      <Text style={s.note}>
        {report.claims.counts.accurate} accurate, {report.claims.counts.inaccurate} inaccurate, {report.claims.counts.unverifiable} not checkable
        against the product page.
      </Text>
      {report.claims.inaccurate.map((c, i) => (
        <View key={i} style={s.item} wrap={false}>
          <Text style={[s.itemTitle, { color: RED }]}>“{c.claim}”</Text>
          {c.note ? <Text style={s.itemSub}>{c.note}</Text> : null}
        </View>
      ))}

      {branded.length ? (
        <>
          <Text style={s.h2}>Branded research</Text>
          {report.branded.map((b) => (
            <View key={b.key} style={s.item} wrap={false}>
              <Text style={s.itemTitle}>{b.text}</Text>
              {Object.keys(b.platforms).map((k) =>
                b.platforms[k].summary ? (
                  <Text key={k} style={s.itemSub}>
                    {AIP_PLATFORM_LABELS[k] || k}: {b.platforms[k].summary}
                  </Text>
                ) : null,
              )}
            </View>
          ))}
        </>
      ) : null}

      {report.subject && report.subject.url ? (
        <Text style={s.note}>
          Product page: <Link src={report.subject.url} style={s.link}>{report.subject.url}</Link>
        </Text>
      ) : null}
    </>
  );
}

function BrandPages({ report }: { report: AiPresenceBrandReport }) {
  return (
    <>
      <Text style={s.h2}>By platform</Text>
      <PlatformTable platforms={report.platforms} />
      <Text style={s.h2}>Products</Text>
      <View style={s.rowHead}>
        <Text style={[s.c1, s.bold]}>Product</Text>
        <Text style={[s.c2, s.bold]}>AI Presence</Text>
        <Text style={[s.c3, s.bold]}>Most visible competitor</Text>
      </View>
      {report.per_product.map((p) => (
        <View key={p.audit_id} style={s.row} wrap={false}>
          <Text style={s.c1}>{p.title}</Text>
          <Text style={[s.c2, s.bold, { color: tone(p.overall_score) }]}>{fmt(p.overall_score)}</Text>
          <Text style={s.c3}>{p.top_competitor ? `${p.top_competitor.name} (${pct(p.top_competitor.appearance_rate)})` : '—'}</Text>
        </View>
      ))}
      <Text style={s.h2}>Top opportunities</Text>
      <Opportunities items={report.top_opportunities} />
      <Text style={s.h2}>Competitors across products</Text>
      {report.competitors.slice(0, 10).map((c) => (
        <Bar key={c.name} name={`${c.name} · ${c.products} product${c.products === 1 ? '' : 's'}`} value={c.avg_appearance_rate} />
      ))}
    </>
  );
}

export function AiPresencePdf({ audit }: { audit: AiPresenceAudit }) {
  const { report } = audit;
  const subject = aipSubjectLabel(audit);
  const meta = report && report.kind === 'product' ? (report as AiPresenceProductReport).meta : null;
  const cards = [
    { label: 'AI Presence', value: audit.overall_score, caption: '50% Discovery + 50% Recommendation' },
    { label: 'Discovery', value: audit.discovery_score, caption: 'Unbranded answers that mention the product' },
    { label: 'Recommendation', value: audit.recommendation_score, caption: 'Recommended 100 · option 50 · otherwise 0' },
  ];
  const renderBody = () => {
    if (!report) {
      return <Text style={s.note}>{audit.status === 'error' ? `This analysis failed: ${audit.generation_error || 'unknown error'}.` : 'Report not available.'}</Text>;
    }
    if (report.kind === 'brand') return <BrandPages report={report as AiPresenceBrandReport} />;
    return <ProductPages report={report as AiPresenceProductReport} />;
  };
  return (
    <Document title={`AI Presence — ${subject || audit.id}`}>
      <Page size="A4" style={s.page}>
        <Text style={s.kicker}>The Desire Company · AI Presence {audit.kind === 'brand' ? 'Brand Analysis' : 'Analysis'}</Text>
        <Text style={s.title}>{subject}</Text>
        <Text style={s.meta}>
          {audit.kind === 'product' && audit.params.brand_name ? `${audit.params.brand_name} · ` : ''}
          {audit.scored_at ? new Date(audit.scored_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
          {meta ? ` · ${meta.surface_label} · ${meta.unbranded_count} category + ${meta.branded_count} product question${meta.branded_count === 1 ? '' : 's'}${meta.samples > 1 ? `, each asked ${meta.samples} times` : ''}` : ''}
        </Text>
        <View style={s.strip}>
          {cards.map((c, i) => (
            <View key={c.label} style={i === cards.length - 1 ? [s.card, s.cardLast] : s.card}>
              <Text style={s.cardLabel}>{c.label}</Text>
              <Text style={[s.cardValue, { color: tone(c.value) }]}>{c.value == null ? '—' : String(c.value)}</Text>
              <Text style={s.cardCaption}>{c.caption}</Text>
            </View>
          ))}
        </View>

        {renderBody()}

        <Text style={s.disclaimer}>
          Answers come from each vendor&apos;s API with web search on: a benchmark that approximates, but isn&apos;t identical to, the consumer
          apps. AI answers vary between runs, so the score spreads across several different estimated shopping questions.
        </Text>
        <View style={s.footer} fixed>
          <Text>AI Presence · The Desire Company · Informational; may contain inaccuracies.</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}
